import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { Flex } from '@chakra-ui/layout';
import { FormHandles } from '@unform/core';
import { isAfter, format } from 'date-fns';
import { AuthorizeView, AutocompleteInput, Button, MaskInput, Unform } from 'components';
import { routePaths } from 'constants/routes';
import { debounce } from 'utils/debounce';
import { objToQuery } from 'utils/query';
import { getAllClients, IGetClientsResponse } from 'services/client';
import { AddHoursModal, ReportsGenerationModal } from './components';
import { formatDateToUs } from 'utils/date';
import { getLoggedUser } from 'utils/storage';
import { checkPermission } from 'utils/authentication';

interface IFormData {
  idCliente: string;
  periodoInicial?: string;
  periodoFinal?: string;
}

interface IProps {
  history: RouteComponentProps['history'];
}

const HeaderComponent: React.FC<IProps> = ({ history }) => {
  const [hasClientSelect, setHasClientSelect] = useState<boolean>(false);
  const [clients, setClients] = useState<IGetClientsResponse[]>([]);
  const [openAddHours, setOpenAddHours] = useState<boolean>(false);
  const [openReportsGen, setOpenReportsGen] = useState<boolean>(false);
  const [dateRangeError, setDateRangeError] = useState<boolean>(false);
  const formRef = useRef<FormHandles>(null);
  const user = useMemo(() => getLoggedUser(), []);

  const handleSearch = useCallback(() => {
    const formData = formRef.current?.getData() as IFormData;

    setHasClientSelect(!!formData.idCliente);

    if (formData?.periodoInicial?.length !== 7) {
      delete formData.periodoInicial;
    }

    if (formData?.periodoFinal?.length !== 7) {
      delete formData.periodoFinal;
    }

    if (formData.periodoInicial && formData.periodoFinal) {
      const after = isAfter(
        new Date(formatDateToUs(`01/${formData.periodoInicial}`)),
        new Date(formatDateToUs(`01/${formData.periodoFinal}`)),
      );

      if (after) {
        delete formData.periodoInicial;
        delete formData.periodoFinal;

        setDateRangeError(true);
      } else {
        setDateRangeError(false);
      }
    }
    const query = objToQuery(formData);

    history.push(`${routePaths.budgetControl}?${query}&page=0`);
  }, [formRef]);

  const handleSearchClient = useCallback(async (search?: string) => {
    try {
      const query = objToQuery({ name: search });

      const response = await getAllClients(query);

      setClients(response.data);
    } catch (error) {
      setClients([]);
    }
  }, []);

  useEffect(() => {
    handleSearchClient();
  }, []);

  useEffect(() => {
    if (user.admin && user.client) setTimeout(handleSearch, 600);
  }, [user.client, handleSearch]);

  const date = new Date();
  const currentMounth = date.getMonth() + 1;
  const lastYear = date.getFullYear() - 1;
  const currentDate = `${lastYear}/${currentMounth}/${date.getDate()}`;

  return (
    <Flex gridArea="header" gridGap="1rem">
      <Flex flex={1}>
        <Unform
          formRef={formRef}
          // input de data do controle orçamentario ja existe um valor default
          initialData={{
            periodoInicial: format(new Date(currentDate), 'MM/yyyy'),
            periodoFinal: format(new Date(), 'MM/yyyy'),
            idCliente: getLoggedUser().client,
          }}
        >
          <Flex gridGap="1rem">
            <AuthorizeView onlyAdmin>
              <Flex flex={2}>
                <AutocompleteInput
                  name="idCliente"
                  placeholder={getLoggedUser().client ? getLoggedUser().clientName : 'Cliente'}
                  options={
                    clients
                      ?.map(client => ({ name: client.name, id: client.id }))
                      .sort((a, b) => {
                        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                      }) || []
                  }
                  optionLabel="name"
                  optionValue="id"
                  notErrorMargin
                  disabled={!!getLoggedUser().client}
                  // @ts-ignore
                  defaultValue={getLoggedUser().client || undefined}
                  onSelectOption={debounce(handleSearch, 100)}
                />
              </Flex>
            </AuthorizeView>
            <Flex flex={2}>
              <MaskInput
                leftIcon="calendar"
                name="periodoInicial"
                mask="period"
                placeholder="Período inicial"
                notErrorMargin
                parseDate
                onChange={debounce(handleSearch, 1000)}
                hasError={dateRangeError}
              />
            </Flex>

            <Flex flex={2}>
              <MaskInput
                placeholder="Período final"
                leftIcon="calendar"
                name="periodoFinal"
                mask="period"
                notErrorMargin
                parseDate
                onChange={debounce(handleSearch, 1000)}
                hasError={dateRangeError}
              />
            </Flex>
            <AuthorizeView onlyClient>
              <Flex flex={2} />
            </AuthorizeView>
          </Flex>
        </Unform>
      </Flex>
      <Flex ml="auto" gridGap="1rem" width={'35%'} justifyContent={'flex-end'}>
        <Button
          style={{ width: '100%' }}
          onClick={() => setOpenReportsGen(true)}
          variant="secondary"
          outline
          leftIcon="download"
        >
          Relatório de serviços
        </Button>

        {checkPermission({ onlyAdmin: true }) && !getLoggedUser().client && (
          <Button
            style={{ width: '100%' }}
            variant="secondary"
            outline
            leftIcon="clock"
            disabled={!hasClientSelect}
            onClick={() => setOpenAddHours(true)}
          >
            Adicionar Horas
          </Button>
        )}
      </Flex>

      <AddHoursModal history={history} open={openAddHours} onClose={() => setOpenAddHours(false)} />
      <ReportsGenerationModal
        history={history}
        open={openReportsGen}
        onClose={() => setOpenReportsGen(false)}
      />
    </Flex>
  );
};

export const Header = memo(HeaderComponent);
